// Modules
import React, { useState, useEffect, useMemo } from "react";

// MUI
import {
  Typography,
  Button,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Link,
} from "@mui/material";
import { Row, Col } from "react-bootstrap";
import CallIcon from "@mui/icons-material/Call";
import EmailIcon from "@mui/icons-material/Email";
import ClientTheme from "../../core/clientTheme";
import OpenNewIcon from "@mui/icons-material/OpenInNewOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { getThemeJson } from "../../utils/helpers";
import AlertModal from "../alertModal";

import { assetBase, serverUrl } from "../../config";

import { Chart } from "chart.js";

import {
  getFontSize,
  getColor,
  getAlignment,
  processCellStyle,
  getOverlay,
  getMargins,
  getDimensions,
} from "./entityBlockParserHelpers";

// You need to use dangerouslySetInnerHTML because block data is HTML (br tags etc...)

const checkEmpty = (data) => {
  if (data === null || data === undefined) return true;
  if (typeof data === "string") return data.trim().length === 0;
  return false;
};

function BlockWrapper({ block, children }) {
  return <div style={{ display: "flow-root" }}>{children}</div>;
}

function TitleParser({ block }) {
  if (checkEmpty(block?.data?.text)) return null;

  return (
    <div className="slide-title-container" style={{ ...getMargins(block) }}>
      <Typography
        className="slide-title"
        owner="client"
        variant="h1"
        singles="true"
        sx={{ ...getFontSize(block) }}
        dangerouslySetInnerHTML={{ __html: block.data.text }}
        // your other parameters like theme etc....
      />
    </div>
  );
}

function SubtitleParser({ block }) {
  if (checkEmpty(block?.data?.text)) return null;

  return (
    <div className="slide-subtitle-container" style={{ ...getMargins(block) }}>
      <Typography
        className="slide-title"
        owner="client"
        variant="h2"
        singles="true"
        dangerouslySetInnerHTML={{ __html: block.data.text }}
        // your other parameters like theme etc....
      />
    </div>
  );
}

function BodyParser({ block }) {
  if (checkEmpty(block?.data?.text)) return null;

  return (
    <div className="slide-body-container" style={{ ...getMargins(block) }}>
      <Typography
        className="slide-body"
        owner="client"
        variant="body2"
        singles="true"
        weight="light"
        lh="wide"
        dangerouslySetInnerHTML={{ __html: block.data.text }}
      />
    </div>
  );
}

// `raw` carries a chunk of pre-existing HTML rather than EditorJS-structured
// text: the backend wraps legacy DB bodies this way (see PollCrudResource's
// docblock) and entity_zip.php rewrites asset URLs inside block.data.html at
// publish time, so it's a first-class block type. Without an entry in PARSERS
// the lookup returns undefined and the block is silently dropped — which left
// the description blank on 7 of 8 polls.
function RawParser({ block }) {
  if (checkEmpty(block?.data?.html)) return null;

  return (
    <div className="slide-body-container" style={{ ...getMargins(block) }}>
      <Typography
        className="slide-body"
        owner="client"
        variant="body2"
        singles="true"
        weight="light"
        lh="wide"
        dangerouslySetInnerHTML={{ __html: block.data.html }}
      />
    </div>
  );
}

function ListParser({ block }) {
  function createItems(items) {
    if (!items?.length) return null;

    const listItems = items
      .map((element, index) => {
        const isObject = element !== null && typeof element === "object";
        const hasEl = isObject ? element.content?.trim().length > 0 : false;
        const hasSubItems = element.items?.length > 0;

        if (isObject) {
          if (!hasEl && !hasSubItems) return null;
        } else {
          if (checkEmpty(element)) return null;
        }

        const color = getColor(block);

        return (
          <li
            key={index}
            style={{ color, ...getFontSize(block) }}
            className={[color ? "li-custom" : null].filter(Boolean).join(" ")}
          >
            {isObject ? (
              <>
                <Typography
                  owner="client"
                  variant="body2"
                  singles="true"
                  weight="light"
                  lh="wide"
                  dangerouslySetInnerHTML={{ __html: element.content }}
                />
                {hasSubItems && createItems(element.items)}
              </>
            ) : (
              <Typography
                owner="client"
                variant="body2"
                singles="true"
                weight="light"
                lh="wide"
                dangerouslySetInnerHTML={{ __html: element }}
              />
            )}
          </li>
        );
      })
      .filter(Boolean);

    return block?.data?.style === "ordered" ? (
      <ol>{listItems}</ol>
    ) : (
      <ul>{listItems}</ul>
    );
  }

  return (
    <div
      className={["list_block", "content_block", getAlignment(block)]
        .filter(Boolean)
        .join(" ")}
      style={{ ...getMargins(block) }}
    >
      {createItems(block?.data?.items)}
    </div>
  );
}

function EntityTableParser({ block, theme_json }) {
  const styled = block.data?.styled;
  const rowCount = block.data.content.length;

  const firstColumnWidthStyle = () => {
    if (styled?.customFirstColWidth) {
      return { gridTemplateColumns: `minmax(5%, ${styled.firstColWidth}%)` };
    }
    return null;
  };

  const firstRowHeightStyle = () => {
    if (styled?.customFirstRowHeight) {
      return {
        gridTemplateRows: `${styled.firstRowHeight}% repeat(${rowCount - 1}, 1fr)`,
      };
    }
    return null;
  };

  const icons = theme_json?.layouts.find((o) => o.id === "data-table")?.icons;

  const rows = block.data.content.map((row, rowIndex) => {
    const cellCount = row.length;

    const cells = row.map((cell, cellIndex) => {
      const cellStyle = processCellStyle(
        rowCount,
        rowIndex,
        cellCount,
        cellIndex,
        block,
        "custom",
      );

      const iconMatch = cell.toLowerCase().match(/\?(.*)(?=\?)/);
      const iconSVG =
        iconMatch && icons?.find((el) => el.name === iconMatch[1]);

      return (
        <div key={cellIndex} className={`tc-cell`} style={cellStyle}>
          {iconSVG ? (
            <div
              className={`${iconSVG.name}-icon icon-container`}
              dangerouslySetInnerHTML={{ __html: iconSVG.icon }}
            />
          ) : (
            <Typography
              owner="client"
              variant="overline"
              singles="true"
              weight="light"
              notransform={true}
              // The resolved cell font-weight (Bold=700 / Light=300) lives on
              // the parent .tc-cell, but the Typography's own weight class wins
              // over inheritance - apply it inline here so it actually renders.
              style={
                cellStyle?.fontWeight
                  ? { fontWeight: cellStyle.fontWeight }
                  : undefined
              }
              dangerouslySetInnerHTML={{ __html: cell }}
            />
          )}
        </div>
      );
    });

    return (
      <div
        key={rowIndex}
        className="tc-row"
        style={{ ...firstColumnWidthStyle() }}
      >
        {cells}
      </div>
    );
  });

  const color = getColor(block);

  return (
    <div
      className={[
        "tc-table",
        block?.data?.withHeadings ? "tc-table--heading" : null,
        "content_block",
        getAlignment(block),
      ]
        .filter(Boolean)
        .join(" ")}
      style={{
        color,
        ...firstRowHeightStyle(),
        ...getMargins(block),
        ...getDimensions(block),
      }}
    >
      {rows}
    </div>
  );
}

function EntityImageParser({ block, json, live, entityType, postId }) {
  let url = block?.data?.url;
  if (!url) return null;

  const imageBlocks =
    json?.blocks.filter((_block) => _block.type === "image") ?? [];
  const imageIndex =
    imageBlocks.findIndex((_block) => _block.id === block.id) + 1;

  if (live) {
    url = `${assetBase}/${entityType}/${postId}/${postId}/${url}`;
  }

  const overlay = getOverlay(block);
  const backgroundColor = overlay
    ? { backgroundColor: overlay.backgroundColor }
    : {};
  return (
    <div
      data-block={imageIndex}
      className={["image_block", "content_block", getAlignment(block)]
        .filter(Boolean)
        .join(" ")}
      style={{
        ...backgroundColor,
        ...getMargins(block),
        ...getDimensions(block),
      }}
    >
      <img
        src={url}
        data-mid={block?.data?.mid}
        alt={`image-${block?.data?.mid}`}
        style={overlay ? { opacity: overlay.opacity } : undefined}
      />
      <Typography
        owner="client"
        variant="body2"
        singles="true"
        weight="light"
        lh="wide"
        className="caption"
      >
        {block.data.caption}
      </Typography>
    </div>
  );
}

function EntityVideoParser({ block, json, live, entityType, postId }) {
  const isRemote = block?.data?.remote;

  let url;
  if (isRemote) {
    url = live ? block?.data?.original_url : block?.data?.url;
  } else {
    url = live
      ? `${assetBase}/${entityType}/${postId}/${postId}/${block?.data?.url}`
      : block?.data?.url;
  }

  let poster;
  if (block?.data?.poster) {
    poster =
      block?.data?.external || isRemote
        ? block.data.poster
        : `${serverUrl}/api/video/thumbnail?mid=${block.data.mid}`;
  }

  return (
    <div
      className={["video_block", "content_block", getAlignment(block)]
        .filter(Boolean)
        .join(" ")}
      style={{ ...getMargins(block), ...getDimensions(block) }}
    >
      {isRemote ? (
        <div
          className="plyr__video-embed"
          data-mid={block.data.mid}
          data-poster={poster}
        >
          <iframe
            src={url}
            allowFullScreen
            allow="autoplay; encrypted-media"
            title={block.data.name || "External Video"}
          />
        </div>
      ) : (
        <video
          className="plyr"
          data-src={url}
          data-mid={block?.data?.mid}
          data-poster={poster}
          playsInline
          controls
        >
          <source src={url} type="video/mp4" />
        </video>
      )}
    </div>
  );
}

function EmbedParser({ block }) {
  return (
    <div
      className={["embed", "content_block", getAlignment(block)]
        .filter(Boolean)
        .join(" ")}
      style={{ ...getMargins(block), ...getDimensions(block) }}
    >
      <div className="embed-container">
        <div
          className="embed"
          dangerouslySetInnerHTML={{ __html: block.data.code }}
        />
      </div>
    </div>
  );
}

export function ButtonGroupParser({ block }) {
  const widthUnit = block.data?.percentage ? "%" : "px";

  return (
    <div
      className={[
        "content_block",
        "button-group_block",
        block.data.single ? "single" : null,
      ]
        .filter(Boolean)
        .join(" ")}
      style={{ ...getMargins(block), ...getDimensions(block) }}
    >
      <div className="btn-group-container">
        {block.data.buttonData.map((e, index) => (
          <div key={index} className="btn-item-container">
            <a
              href={e.url}
              className="btn-item-link-wrapper"
              target="_blank"
              rel="noopener noreferrer"
            >
              <button
                className="btn-item"
                style={{ width: `${block.data.buttonWidth}${widthUnit}` }}
              >
                {e.text}
              </button>
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}

export function RuleParser({ block }) {
  return (
    <div
      className={["rule_block", "content_block", getAlignment(block)]
        .filter(Boolean)
        .join(" ")}
      style={{ ...getMargins(block), ...getDimensions(block) }}
    >
      <div className="rule_block_redactor">
        <hr style={{ color: getColor(block) }} />
      </div>
    </div>
  );
}

export function ChartParser({ block }) {
  return (
    <div
      className="chart-block"
      style={{ ...getMargins(block), ...getDimensions(block) }}
    >
      <canvas data-chart-id={block.id} />
    </div>
  );
}

export function EntityMapParser({
  block,
  postId,
  json,
  theme_json,
  themeDirectory,
}) {
  const apiKey = theme_json?.googleApiKey;
  const content = apiKey ? (
    <iframe
      title={`map-${postId}-${block?.data?.label.replace(" ", "-")}`}
      src={`/map.html?key=${apiKey}&lat=${block?.data?.latitude}&lng=${block?.data?.longitude}&label=${encodeURIComponent(block?.data?.label)}&pin=${themeDirectory}/dist/images/icons/${theme_json?.icons?.pin}&zoom=${block?.data?.zoomLevel ?? 16}&entity=true`}
    />
  ) : (
    <div className="d-flex h-100 justify-content-center align-items-center">
      <b>Map can't be displayed - API key error</b>
    </div>
  );

  return (
    <div
      className="map"
      style={{ ...getMargins(block), ...getDimensions(block) }}
    >
      <div
        id={`map-${postId}`}
        className={["w-100", "h-100", "map-container", getAlignment(block)]
          .filter(Boolean)
          .join(" ")}
      >
        {content}
      </div>
    </div>
  );
}

export function QuestionParser({
  block,
  setQuizAnswers,
  quizEntitySubmission,
  quizUpdate,
  quizProgress,
  onComplete,
  post,
}) {
  const question = block?.data?.field_question ?? "";
  const options = Array.isArray(block?.data?.field_answers)
    ? block.data.field_answers
    : [];
  const type = (block?.data?.type ?? "Single answer")
    .replace(" ", "-")
    .toLowerCase();
  const maxSelectable =
    type === "multiple-answer"
      ? options.filter((o) => o?.field_correct === true).length
      : 1;

  const [selected, setSelected] = useState(new Set());
  const [maxAnswerReachedAlert, setMaxAnswerReachedAlert] = useState(false);

  const submittedAnswers = useMemo(() => {
    if (!quizEntitySubmission?.field_question) return null;
    return quizEntitySubmission.field_question
      .flatMap((q) => q?.field_answers ?? [])
      .filter(Boolean);
  }, [quizEntitySubmission]);

  useEffect(() => {
    if (!Array.isArray(quizProgress)) return;
    const restored = quizProgress
      .filter((p) => p?.question_id === block.id && p?.checked)
      .map((p) => p.answer_id);
    if (restored.length > 0) {
      setSelected(new Set(restored));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizProgress]);

  useEffect(() => {
    if (quizEntitySubmission !== null) return;
    if (!question || options.length === 0) return;
    setQuizAnswers((prev) => {
      const safeAnswers = Array.isArray(prev) ? prev : [];
      const updatedQuestion = {
        field_question: question,
        questionId: block.id,
        type,
        field_answers: options.map((option) => ({
          field_answer: option?.field_answer ?? "",
          field_correct: selected.has(option?.id),
          id: option?.id ?? null,
        })),
      };
      const existingIndex = safeAnswers.findIndex(
        (q) => q?.field_question === question,
      );
      if (existingIndex !== -1) {
        const next = [...safeAnswers];
        next[existingIndex] = updatedQuestion;
        return next;
      }
      return [...safeAnswers, updatedQuestion];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  useEffect(() => {
    if (!onComplete) return;
    onComplete(block.id, selected.size >= maxSelectable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const handleClick = (optionId) => {
    if (!optionId) return;
    if (type === "single-answer") {
      setSelected((prev) =>
        prev.has(optionId) ? new Set() : new Set([optionId]),
      );
      return;
    }
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(optionId)) {
        next.delete(optionId);
      } else if (next.size < maxSelectable) {
        next.add(optionId);
      }
      return next;
    });
  };

  const getOptionStyle = (option, isDisabled) => {
    if (quizEntitySubmission && submittedAnswers) {
      if (option.field_correct) return { color: "#66bb6a" };
      if (getOptionChecked(option)) return { color: "#f44336" };
    }
    if (isDisabled) return { color: "#cdcdcd" };
    return undefined;
  };

  const getOptionChecked = (option) => {
    if (quizEntitySubmission && submittedAnswers) {
      return submittedAnswers.find((a) => a?.id === option.id)?.field_correct;
    }
    return selected.has(option.id);
  };

  if (!block?.data) return null;

  return (
    <div
      className={`question-container ${type}`}
      data-block-id={block?.id}
      style={{ ...getMargins(block) }}
    >
      <h4 className="question">{question}</h4>
      <h6 className="question-type">
        {type === "single-answer" ? "Choose one" : `Choose ${maxSelectable}`}
      </h6>
      <FormGroup className="options-container">
        {options.map((option) => {
          if (!option?.id) return null;
          const isSelected = selected.has(option.id);
          const isDisabled =
            !!quizEntitySubmission ||
            (!isSelected && selected.size >= maxSelectable);
          const style = getOptionStyle(option, isDisabled);
          const checked = getOptionChecked(option);

          return (
            <FormControlLabel
              className="my-2"
              key={option.id}
              control={
                <Checkbox
                  className="p-0 mx-2"
                  color="clientPrimary"
                  disabled={!!quizEntitySubmission}
                  checked={checked}
                  style={style}
                  onChange={(event) => {
                    if (isDisabled) {
                      setMaxAnswerReachedAlert(true);
                    } else {
                      handleClick(option.id);
                      quizUpdate({
                        id: post.id,
                        question_id: block.id,
                        answer_id: option.id,
                        checked: event.target.checked,
                      });
                    }
                  }}
                />
              }
              label={
                <Typography
                  owner="client"
                  variant="body1"
                  lh="medium"
                  style={style}
                >
                  {option?.field_answer ?? ""}
                </Typography>
              }
            />
          );
        })}
      </FormGroup>
      <AlertModal
        warningIcon
        showAlert={maxAnswerReachedAlert}
        alertMessageTitle={`Maximum number of answers reached.`}
        alertMessageBody={
          "You have reached the maximum number of answers for this question. Uncheck at least one to modify your selection"
        }
        confirmButton={true}
        confirmButtonLabel={"Ok"}
        onConfirm={() => {
          setMaxAnswerReachedAlert(false);
        }}
      />
    </div>
  );
}

export function TrainingSectionParser({
  block,
  setFieldReferences,
  trainingEntitySubmission,
  trainingUpdate,
  trainingProgress,
  onComplete,
  preview,
  post,
}) {
  const entities = block?.data?.entities ?? [];
  const isSubmitted = !!trainingEntitySubmission;

  const [opened, setOpened] = useState(new Map());
  const [finished, setFinished] = useState(new Map());

  const submittedEntities = useMemo(
    () => trainingEntitySubmission?.field_references ?? null,
    [trainingEntitySubmission],
  );

  useEffect(() => {
    if (isSubmitted) return;
    if (!Array.isArray(trainingProgress) || trainingProgress.length === 0)
      return;

    const completedEntities = trainingProgress
      .filter((p) => p?.complete)
      .map((p) =>
        entities.find(
          (e) => String(e.id) === String(p.id) && e.type === p.contentType,
        ),
      )
      .filter(Boolean);

    if (completedEntities.length === 0) return;

    setOpened((prev) => {
      const next = new Map(prev);
      completedEntities.forEach((e) => next.set(e.id, e));
      return next;
    });

    setFinished((prev) => {
      const next = new Map(prev);
      completedEntities.forEach((e) => next.set(e.id, e));
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trainingProgress, isSubmitted]);

  useEffect(() => {
    if (!setFieldReferences) return;
    setFieldReferences((prev) => {
      const safeAnswers = Array.isArray(prev) ? prev : [];
      const updatedSection = {
        block_id: block.id,
        opened: Array.from(opened.values()),
        finished: Array.from(finished.values()),
      };
      const existingIndex = safeAnswers.findIndex(
        (s) => s.block_id === block.id,
      );
      if (existingIndex !== -1) {
        const next = [...safeAnswers];
        next[existingIndex] = updatedSection;
        return next;
      }
      return [...safeAnswers, updatedSection];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, finished]);

  useEffect(() => {
    if (!onComplete) return;
    onComplete(
      block.id,
      finished.size >= entities.length && entities.length > 0,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished]);

  const handleResourceClick = (entity) => {
    const { type, id } = entity;

    if (!opened.has(id)) {
      setOpened((prev) => new Map(prev).set(id, entity));
    }

    // Guard the array itself: `post?.field_references.length` only guards
    // `post`, so a book.json without the key threw here and no item opened.
    const refs = Array.isArray(post?.field_references)
      ? post.field_references
      : [];
    const ref = preview
      ? null
      : refs.find(
          (item) =>
            String(item.id) === String(entity.id) &&
            item.type_id === entity.type,
        );

    // Presentations carry access_tokens as a list ([{token}]); the companyapp
    // entities carry a single string. Absent in the published book today, so
    // this resolves to "" rather than the literal "undefined" that was being
    // appended to every route.
    const rawToken = ref?.access_token ?? ref?.access_tokens;
    const token = Array.isArray(rawToken) ? rawToken[0]?.token : rawToken;
    const hash = token ? `?hash=${encodeURIComponent(token)}` : "";

    let route = "";
    switch (type) {
      case "news":
        route = `/news/view/${id}${hash}`;
        break;
      case "event":
        route = `/events/view/${id}${hash}`;
        break;
      case "presentation":
        route = `/presentations/view/${id}${hash}`;
        break;
      case "video":
        route = `/videos/view/${id}${hash}`;
        break;
      case "gallery":
        route = `/galleries/view/${id}${hash}`;
        break;
      case "podcast":
        route = `/podcasts/view/${id}${hash}`;
        break;
      case "training":
        route = `/training/view/${id}${hash}`;
        break;
      case "quiz":
        route = `/quizzes/view/${id}${hash}`;
        break;
      case "document":
        route = `/documents/view/${id}${hash}`;
        break;
      case "webform":
        route = `/forms/view/${id}${hash}`;
        break;
      case "link":
        // Links open their external target, which the publisher emits on the
        // matched reference — there is no /links/view/:id route to fall back
        // on, so an older book without the url simply can't be opened.
        route = ref?.url ?? "";
        break;
      default:
        route = "";
        break;
    }

    if (route) window.open(route, "_blank");
  };

  const handleFinishedChange = (entity) => {
    setFinished((prev) => {
      const next = new Map(prev);
      if (next.has(entity.id)) {
        next.delete(entity.id);
      } else {
        next.set(entity.id, entity);
      }
      return next;
    });
  };

  const getCheckboxChecked = (entity) => {
    if (isSubmitted && submittedEntities) {
      return submittedEntities.some(
        (e) => e?.id === entity.id && e?.bundle === entity?.bundle,
      );
    }
    return finished.has(entity.id);
  };

  if (!block?.data) return null;

  return (
    <div className="training-section block" style={{ ...getMargins(block) }}>
      {block?.data?.title && (
        <div className="training-section-title-container mb-3">
          <Typography owner="client" singles="true" variant="h4">
            {block.data.title}
          </Typography>
        </div>
      )}
      {block?.data?.description && (
        <div className="training-section-description-container mb-3">
          <Typography
            owner="client"
            variant="body2"
            singles="true"
            color="clientText.dark"
            lh="wide"
          >
            {block.data.description}
          </Typography>
        </div>
      )}
      <div className="training-section-resources-container">
        {entities.map((entity) => {
          if (!entity?.id) return null;
          const hasOpened = opened.has(entity.id);
          const isFinished = getCheckboxChecked(entity);

          return (
            <div
              className="d-flex align-items-center training-section-resource-item"
              key={`${entity.type}-${entity.id}`}
            >
              <Checkbox
                disabled={!hasOpened || isSubmitted}
                checked={isFinished}
                onChange={(event) => {
                  !isSubmitted && handleFinishedChange(entity);
                  if (trainingUpdate) {
                    trainingUpdate({
                      id: post.id,
                      entity_id: entity.id,
                      contentType: entity.type,
                      checked: event.target.checked,
                    });
                  }
                }}
              />
              <Button
                className="d-flex justify-content-between flex-grow-1"
                onClick={() => handleResourceClick(entity)}
                endIcon={<OpenNewIcon />}
              >
                <Typography>{entity?.title ?? ""}</Typography>
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ContactGroupParser({ block }) {
  const contactName = block?.data?.contactName ?? "";
  const phoneNumbers = (block?.data?.phoneNumbers ?? []).filter(
    (value) => typeof value === "string" && value.trim().length > 0,
  );
  const emails = (block?.data?.emails ?? []).filter(
    (value) => typeof value === "string" && value.trim().length > 0,
  );
  const clientTheme = { ...ClientTheme() };

  if (!contactName && phoneNumbers.length === 0 && emails.length === 0) {
    return null;
  }

  return (
    <Row
      id={`contact-group-${block.id}`}
      className="contact-group-item-container pt-2 pb-3"
      style={{ ...getMargins(block) }}
    >
      <Col>
        {contactName && (
          <Row className="contact-group-name mb-2">
            <Col xs={12}>
              <Typography
                owner="client"
                className="pt-2 mb-3"
                variant="h5"
                color="clientText.main"
                singles="true"
              >
                {contactName}
              </Typography>
            </Col>
          </Row>
        )}

        {phoneNumbers.map((phone, index) => (
          <Row
            key={`phone-${index}`}
            className="contact-group-phone ps-4 mb-2"
          >
            <Col xs={12}>
              <Row className="gx-0 display-flex align-items-center">
                <Col xs="auto" className="pe-3">
                  <CallIcon fontSize="small" color="clientPrimary" />
                </Col>
                <Col>
                  <Typography
                    className="group-item-phone"
                    variant="body2"
                    color="clientText.main"
                  >
                    <Link
                      owner="client"
                      variant="body2"
                      className="d-flex justify-content-between"
                      singles="true"
                      weight="light"
                      lh="x-wide"
                      color="clientText.main"
                      hoverbehavior={clientTheme.components.link.hoverbehavior}
                      href={`tel:${phone}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {phone}
                      <OpenInNewIcon fontSize="small" color="clientPrimary" />
                    </Link>
                  </Typography>
                </Col>
              </Row>
            </Col>
          </Row>
        ))}

        {emails.map((email, index) => (
          <Row
            key={`email-${index}`}
            className="contact-group-mail ps-4 mb-2"
          >
            <Col xs={12}>
              <Row className="gx-0 display-flex align-items-center">
                <Col xs="auto" className="pe-3">
                  <EmailIcon fontSize="small" color="clientPrimary" />
                </Col>
                <Col>
                  <Typography
                    className="group-item-email"
                    variant="body2"
                    color="clientText.main"
                  >
                    <Link
                      owner="client"
                      variant="body2"
                      className="d-flex justify-content-between"
                      singles="true"
                      weight="light"
                      lh="x-wide"
                      color="clientText.main"
                      hoverbehavior={clientTheme.components.link.hoverbehavior}
                      href={`mailto:${email}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {email}
                      <OpenInNewIcon fontSize="small" color="clientPrimary" />
                    </Link>
                  </Typography>
                </Col>
              </Row>
            </Col>
          </Row>
        ))}
      </Col>
    </Row>
  );
}

export default function EntityBlockRenderer({
  blocks,
  editorData,
  preview,
  entityType,
  postId,
  themeDirectory,
  setQuizEntityAnswers,
  quizEntitySubmission,
  quizProgress,
  quizUpdate,
  setFieldReferences,
  trainingEntitySubmission,
  trainingProgress,
  trainingUpdate,
  onComplete,
  post,
}) {
  const [quizAnswers, setQuizAnswers] = useState([]);
  const theme_json = getThemeJson("companyapp");

  useEffect(() => {
    if (editorData.blocks && editorData.blocks.length > 0) {
      if (editorData.blocks.some((e) => e.type === "chart")) {
        editorData.blocks.forEach((f) => {
          if (f.type === "chart") {
            const chartCanvas = document.querySelector(
              `[data-chart-id="${f.id}"]`,
            );

            // Updated renderPieChart to accept parameters
            const renderPieChart = (chartCanvas, chartData) => {
              if (!chartCanvas) return;

              const { labels, datasets, dataSetColors } = chartData;

              const ctx = chartCanvas.getContext("2d");

              datasets.forEach((g) => {
                g.backgroundColor = dataSetColors;
              });

              new Chart(ctx, {
                type: "pie",
                data: {
                  labels,
                  datasets,
                },
                options: {
                  responsive: true,
                  animation: false,
                  // Fill the container so the circle is centred (like bar/line);
                  // without this the square canvas left-aligns.
                  maintainAspectRatio: false,
                },
              });
            };

            const renderBarChart = (chartCanvas, chartData) => {
              if (!chartCanvas) return;

              const { labels, datasets } = chartData;

              const ctx = chartCanvas.getContext("2d");

              datasets.forEach((e, i) => {
                e.backgroundColor = e.seriesColor;
              });

              new Chart(ctx, {
                type: "bar",
                data: {
                  labels,
                  datasets,
                },
                options: {
                  responsive: true,
                  animation: false,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: "top",
                    },
                  },
                },
              });
            };

            const renderLineChart = (chartCanvas, chartData) => {
              if (!chartCanvas) return;

              const { labels, datasets } = chartData;

              const ctx = chartCanvas.getContext("2d");

              datasets.forEach((e, i) => {
                e.backgroundColor = e.seriesColor;
                e.borderColor = e.seriesColor;
              });

              new Chart(ctx, {
                type: "line",
                data: {
                  labels,
                  datasets,
                },
                options: {
                  responsive: true,
                  maintainAspectRatio: false,
                  animation: false,
                  plugins: {
                    legend: {
                      position: "top",
                    },
                  },
                },
              });
            };

            const renderDonutChart = (chartCanvas, chartData) => {
              if (!chartCanvas) return;

              const { labels, datasets, dataSetColors } = chartData;

              const ctx = chartCanvas.getContext("2d");

              datasets.forEach((e, i) => {
                e.backgroundColor = dataSetColors;
              });

              new Chart(ctx, {
                type: "doughnut",
                data: {
                  labels,
                  datasets,
                },
                options: {
                  responsive: true,
                  animation: false,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: "top",
                    },
                  },
                },
              });
            };
            const _chartData = processChartData(f);
            switch (f.data.type) {
              case "pie":
                renderPieChart(chartCanvas, _chartData);
                break;
              case "bar":
                renderBarChart(chartCanvas, _chartData);
                break;
              case "line":
                renderLineChart(chartCanvas, _chartData);
                break;
              case "doughnut":
                renderDonutChart(chartCanvas, _chartData);
                break;
              default:
                break;
            }
          } else return;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (setQuizEntityAnswers) {
      setQuizEntityAnswers(quizAnswers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizAnswers]);

  if (!blocks?.length) return null;

  const processChartData = (block) => {
    const _chartData = block.data.chartData;
    const dataSetColors = block.data.datasetColors;
    const labels = [];
    const datasets = [];
    _chartData.forEach((row, index) => {
      if (index === 0) {
        row.dataset.forEach((e, i) => {
          if (i === 0) return;
          labels.push(e);
        });
      } else {
        const data = [];
        row.dataset.forEach((f, j) => {
          if (j === 0) return;
          data.push(Number(f) || 0);
        });
        datasets.push({
          label: row.dataset[0],
          data,
          seriesColor: row.seriesColor,
        });
        return;
      }
    });
    return {
      labels,
      datasets,
      dataSetColors,
    };
  };

  const PARSERS = {
    title: TitleParser,
    subtitle: SubtitleParser,
    list: ListParser,
    body: BodyParser,
    raw: RawParser,
    table: EntityTableParser,
    image: EntityImageParser,
    video: EntityVideoParser,
    embed: EmbedParser,
    rule: RuleParser,
    buttonGroup: ButtonGroupParser,
    map: EntityMapParser,
    question: QuestionParser,
    chart: ChartParser,
    trainingSection: TrainingSectionParser,
    contactGroup: ContactGroupParser,
  };

  return blocks.map((block, index) => {
    const Comp = PARSERS[block.type];
    if (!Comp) return null;
    return (
      <BlockWrapper key={block.id ?? index}>
        <Comp
          setQuizAnswers={setQuizAnswers}
          block={block}
          editorData={editorData}
          preview={preview}
          live={!preview}
          entityType={entityType}
          postId={postId}
          themeDirectory={themeDirectory}
          quizEntitySubmission={quizEntitySubmission}
          quizProgress={quizProgress}
          quizUpdate={quizUpdate}
          setFieldReferences={setFieldReferences}
          trainingUpdate={trainingUpdate}
          trainingEntitySubmission={trainingEntitySubmission}
          trainingProgress={trainingProgress}
          onComplete={onComplete}
          theme_json={theme_json}
          post={post}
        />
      </BlockWrapper>
    );
  });
}
